import { onMounted, onUnmounted } from "vue";

import { useGlobalSearch } from "./useGlobalSearch";

/**
 * Register global keyboard shortcuts for search.
 *
 * Shortcuts:
 * - Cmd+K / Ctrl+K: Open search
 * - /: Open search (only when not in input)
 * - Escape: Close search (handled by CommandDialog)
 */
export function useGlobalKeyboardShortcuts() {
  const { open, close, isOpen } = useGlobalSearch();

  /**
   * Check if the active element is an input-like element.
   */
  function isInputElement(): boolean {
    const activeElement = document.activeElement;
    if (!activeElement) return false;

    const tagName = activeElement.tagName.toLowerCase();
    const isContentEditable = activeElement.getAttribute("contenteditable") === "true";

    return (
      tagName === "input" ||
      tagName === "textarea" ||
      tagName === "select" ||
      isContentEditable
    );
  }

  /**
   * Handle keyboard events.
   */
  function handleKeyDown(event: KeyboardEvent) {
    // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      if (isOpen.value) {
        close();
      } else {
        open();
      }
      return;
    }

    // / key (only when not in input)
    if (event.key === "/" && !isInputElement() && !isOpen.value) {
      event.preventDefault();
      open();
      return;
    }

    // Escape key (close if open)
    if (event.key === "Escape" && isOpen.value) {
      event.preventDefault();
      close();
      return;
    }
  }

  /**
   * Register event listeners on mount.
   */
  onMounted(() => {
    document.addEventListener("keydown", handleKeyDown);
  });

  /**
   * Remove event listeners on unmount.
   */
  onUnmounted(() => {
    document.removeEventListener("keydown", handleKeyDown);
  });
}
